import React from "react";
import { AlignLeft, AlignCenter, AlignRight, Minus, Plus, RotateCcw } from "lucide-react";

export const TEXT_ROLES = {
  title: "Titre principal", subtitle: "Sous-titre", description: "Description",
  bullets: "Liste à puces", page_number: "Numéro de page", eyebrow: "Chapitre",
  annotation: "Annotation", signature: "Signature / marque",
};

export const NumberControl = ({ id, label, value, onChange, min, max, step = 1, unit = "", buttons = false }) => {
  const [input, setInput] = React.useState(String(value));
  React.useEffect(() => { setInput(String(value)); }, [value]);
  const commit = raw => {
    setInput(raw);
    const n = Number(raw);
    if (raw !== "" && Number.isFinite(n) && n >= min && n <= max) onChange(n);
  };
  return <div className="typo-number-row">
    <label htmlFor={`${id}-number`}>{label} <span>{unit}</span></label>
    <div className="typo-number-fields">
      <input data-testid={`${id}-slider`} aria-label={`${label} — curseur`} type="range" min={min} max={max}
        step={step} value={value} onChange={e => onChange(Number(e.target.value))}/>
      {buttons && <button type="button" data-testid={`${id}-minus`} aria-label={`Diminuer ${label}`} title={`Diminuer ${label}`}
        onClick={() => onChange(Math.max(min, value - step))}><Minus size={15}/></button>}
      <input id={`${id}-number`} data-testid={`${id}-number`} aria-label={label} type="number" min={min} max={max}
        step={step} value={input} onChange={e => commit(e.target.value)}
        onBlur={() => { const n = Number(input); onChange(Math.max(min, Math.min(max, Number.isFinite(n) && input !== "" ? n : value))); setInput(String(value)); }}/>
      {buttons && <button type="button" data-testid={`${id}-plus`} aria-label={`Augmenter ${label}`} title={`Augmenter ${label}`}
        onClick={() => onChange(Math.min(max, value + step))}><Plus size={15}/></button>}
    </div>
  </div>;
};

export const TypographyControls = ({ id, role, setRole, scope, setScope, style, onChange, onReset, overridden }) => {
  const set = (key, value) => onChange({ [key]: value });
  return <section className="typography-controls" data-testid={`${id}-typography`}>
    <div className="typo-section-heading"><h4>Typographie</h4>
      <button data-testid={`${id}-reset-${role}`} title="Réinitialiser cet élément" aria-label="Réinitialiser cet élément" onClick={onReset}><RotateCcw size={16}/></button></div>
    <div className="typo-scopes" role="group" aria-label="Portée des réglages">
      {[['individual', 'Cet élément'], ['global', 'Toutes les pages']].map(([key, text]) =>
        <button data-testid={`${id}-scope-${key}`} key={key} aria-pressed={scope === key} onClick={() => setScope(key)}>{text}</button>)}
    </div>
    <label className="typo-select-label" htmlFor={`${id}-role`}>Élément
      <select id={`${id}-role`} data-testid={`${id}-element-select`} value={role} onChange={e => setRole(e.target.value)}>
        {Object.entries(TEXT_ROLES).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
      </select>
    </label>
    <div className="typo-inheritance" data-testid={`${id}-inheritance`}>
      {scope === "global" ? "Réglage global · les exceptions individuelles sont conservées" : overridden ? "Personnalisé sur cette page" : "Hérité des réglages globaux"}
    </div>
    <NumberControl id={`${id}-${role}-size`} label="Taille" unit="px" value={style.size} onChange={v => set('size', v)} min={1} max={1080} buttons/>
    <NumberControl id={`${id}-${role}-opacity`} label="Opacité du texte" unit="%" value={style.opacity} onChange={v => set('opacity', v)} min={0} max={100}/>
    <div className="typo-color-row"><label htmlFor={`${id}-${role}-color`}>Couleur du texte</label>
      <input type="color" id={`${id}-${role}-color`} data-testid={`${id}-${role}-color`} value={style.color === 'auto' ? style.resolvedColor : style.color}
        onChange={e => set('color', e.target.value)}/>
      <button data-testid={`${id}-${role}-color-auto`} aria-pressed={style.color === 'auto'} onClick={() => set('color', 'auto')}>Auto</button>
    </div>
    <div className="typo-two-fields">
      <label htmlFor={`${id}-font`}>Police<select id={`${id}-font`} data-testid={`${id}-${role}-font`} value={style.font} onChange={e => set('font', e.target.value)}>
        <option value="serif">Playfair Display</option><option value="sans">Montserrat</option><option value="italic">Lora italique</option>
        <option value="script">Dancing Script</option><option value="hand">Caveat</option>
      </select></label>
      <label htmlFor={`${id}-weight`}>Graisse<select id={`${id}-weight`} data-testid={`${id}-${role}-weight`} value={style.weight} onChange={e => set('weight', Number(e.target.value))}>
        {[100,200,300,400,500,600,700,800,900].map(w => <option key={w} value={w}>{w}{w === 400 ? ' · Normal' : w === 700 ? ' · Gras' : ''}</option>)}
      </select></label>
    </div>
    <NumberControl id={`${id}-${role}-line-height`} label="Interlignage" value={style.line_height} onChange={v => set('line_height', v)} min={1} max={3} step={0.05}/>
    <NumberControl id={`${id}-${role}-letter-spacing`} label="Espacement des lettres" unit="px" value={style.letter_spacing} onChange={v => set('letter_spacing', v)} min={0} max={40} step={0.5}/>
    <NumberControl id={`${id}-${role}-max-width`} label="Largeur maximale" unit="px" value={style.max_width} onChange={v => set('max_width', v)} min={40} max={1080} step={1}/>
    <div className="typo-alignment"><span>Alignement</span><div role="group" aria-label="Alignement du texte">
      {[["left", AlignLeft, "Gauche"], ["center", AlignCenter, "Centre"], ["right", AlignRight, "Droite"]].map(([key, Icon, label]) =>
        <button key={key} data-testid={`${id}-${role}-align-${key}`} aria-label={label} title={label} aria-pressed={style.align === key} onClick={() => set('align', key)}><Icon size={18}/></button>)}
    </div></div>
  </section>;
};